import { useState, FormEvent, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkAccess, saveSession } from '../utils/api';
import BrandLogo from '../components/BrandLogo';

const ProjectIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const LearnIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.4696 11.7893 19.9609 11.4142 19.5858C11.0391 19.2107 10.5304 19 10 19H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.4696 12.2107 19.9609 12.5858 19.5858C12.9609 19.2107 13.4696 19 14 19H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const ContactIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
    </svg>
);

const JoinIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="17" y1="11" x2="23" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

interface PresaleNoticeProps {
    onViewWhitepaper: () => void;
}

const PresaleNotice = ({ onViewWhitepaper }: PresaleNoticeProps) => (
    <div
        style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
            width: 'min(100%, 520px)',
            margin: '0 0 clamp(22px, 3vw, 30px)',
            padding: 'clamp(14px, 2vw, 18px) clamp(16px, 2.4vw, 22px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'linear-gradient(135deg, rgba(20, 25, 33, 0.92), rgba(13, 17, 23, 0.84))',
            boxShadow: '0 18px 40px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(14px)',
            overflow: 'hidden'
        }}
    >
        <div
            style={{
                position: 'absolute',
                inset: '0 auto auto 0',
                width: '100%',
                height: '100%',
                background: 'radial-gradient(circle at top right, rgba(242, 194, 48, 0.18), transparent 42%)',
                pointerEvents: 'none'
            }}
        />

        <div style={{ position: 'relative', zIndex: 1, minWidth: 0, flex: '1 1 240px' }}>
            <div
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '7px 11px',
                    borderRadius: '999px',
                    background: 'rgba(242, 194, 48, 0.12)',
                    border: '1px solid rgba(242, 194, 48, 0.2)',
                    color: '#f6d365',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                }}
            >
                <span
                    aria-hidden="true"
                    style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '999px',
                        background: '#f2c230',
                        boxShadow: '0 0 12px rgba(242, 194, 48, 0.85)'
                    }}
                />
                Presale
            </div>

            <div
                style={{
                    color: '#ffffff',
                    fontSize: 'clamp(1.02rem, 1.7vw, 1.22rem)',
                    fontWeight: 800,
                    lineHeight: 1.35,
                    marginTop: '12px',
                    textWrap: 'balance'
                }}
            >
                Presale on 30 August 2026
            </div>

            <div
                style={{
                    color: 'rgba(255, 255, 255, 0.68)',
                    fontSize: '0.92rem',
                    lineHeight: 1.6,
                    marginTop: '8px',
                    maxWidth: '34ch'
                }}
            >
                Early access opens soon with a premium launch window for the community.
            </div>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                    marginTop: '16px'
                }}
            >
                <button
                    type="button"
                    onClick={onViewWhitepaper}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '11px 16px',
                        borderRadius: '14px',
                        border: '1px solid rgba(242, 194, 48, 0.28)',
                        background: 'linear-gradient(135deg, rgba(242, 194, 48, 0.2), rgba(242, 194, 48, 0.1))',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 10px 22px rgba(242, 194, 48, 0.12)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
                    }}
                >
                    <span>View Whitepaper</span>
                    <span aria-hidden="true" style={{ color: '#f8d978', fontSize: '1rem', lineHeight: 1 }}>
                        {'->'}
                    </span>
                </button>

                <span
                    style={{
                        color: 'rgba(255, 255, 255, 0.52)',
                        fontSize: '0.82rem',
                        lineHeight: 1.5
                    }}
                >
                    Read the project vision and launch roadmap
                </span>
            </div>
        </div>

        <div
            style={{
                position: 'relative',
                zIndex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 14px',
                borderRadius: '18px',
                background: 'linear-gradient(135deg, rgba(242, 194, 48, 0.16), rgba(242, 194, 48, 0.06))',
                border: '1px solid rgba(242, 194, 48, 0.22)',
                flex: '0 0 auto'
            }}
        >
            <span
                style={{
                    color: '#f8d978',
                    fontSize: '1.55rem',
                    fontWeight: 900,
                    lineHeight: 1
                }}
            >
                30
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span
                    style={{
                        color: '#ffffff',
                        fontSize: '0.94rem',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                    }}
                >
                    August
                </span>
                <span
                    style={{
                        color: 'rgba(255, 255, 255, 0.68)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em'
                    }}
                >
                    2026
                </span>
            </div>
        </div>
    </div>
);

const JOIN_ITEM = {
    name: 'Join Us',
    description: 'Join the Community',
    url: '/join',
    Icon: JoinIcon,
    isInternal: true
};

const NAV_ITEMS = [
    {
        name: 'W3LO Project',
        description: 'Project Intro & Whitepaper',
        url: '/project-intro',
        Icon: ProjectIcon,
        isInternal: true
    },
    {
        name: 'Learn About',
        description: 'Training & Guides',
        url: '/learn',
        Icon: LearnIcon,
        isInternal: true
    },
    {
        name: 'Contact Us',
        description: 'Support & Inquiries',
        url: '/contact',
        Icon: ContactIcon,
        isInternal: true
    }
];

interface Particle {
    x: number;
    y: number;
    size: number;
    speedX: number;
    speedY: number;
    opacity: number;
    hue: number;
}

const AnimatedBackground = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const particlesRef = useRef<Particle[]>([]);
    const animationRef = useRef<number>();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener('resize', resize);

        const createParticles = () => {
            const particles: Particle[] = [];
            const particleCount = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));

            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    size: Math.random() * 2 + 0.5,
                    speedX: (Math.random() - 0.5) * 0.3,
                    speedY: (Math.random() - 0.5) * 0.3,
                    opacity: Math.random() * 0.5 + 0.2,
                    hue: Math.random() > 0.5 ? 45 : 210
                });
            }
            return particles;
        };

        particlesRef.current = createParticles();

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            particlesRef.current.forEach((particle) => {
                particle.x += particle.speedX;
                particle.y += particle.speedY;

                if (particle.x < 0) particle.x = canvas.width;
                if (particle.x > canvas.width) particle.x = 0;
                if (particle.y < 0) particle.y = canvas.height;
                if (particle.y > canvas.height) particle.y = 0;

                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = `hsla(${particle.hue}, 70%, 60%, ${particle.opacity})`;
                ctx.fill();
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    return <canvas ref={canvasRef} className="hero-canvas" />;
};

const OPTIMIZING_MESSAGES = [
    'Optimizing network latency...',
    'Synchronizing blockchain data...',
    'Verifying credentials...',
    'Establishing secure connection...',
    'Loading user environment...',
    'Configuring workspace...',
    'Initializing smart contracts...',
    'Connecting to distributed nodes...',
];

const OptimizingOverlay = () => {
    const [messageIndex, setMessageIndex] = useState(0);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const messageInterval = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % OPTIMIZING_MESSAGES.length);
        }, 625);

        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) return 100;
                return prev + 2;
            });
        }, 100);

        return () => {
            clearInterval(messageInterval);
            clearInterval(progressInterval);
        };
    }, []);

    return (
        <div className="optimizing-overlay">
            <div className="optimizing-content">
                <div className="optimizing-logo">
                    <BrandLogo size={48} />
                </div>
                <h2 className="optimizing-title">Referring to the Platform</h2>
                <div className="progress-bar-container">
                    <div className="progress-bar-track">
                        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="progress-percentage">{Math.min(progress, 100)}%</span>
                </div>
                <div className="optimizing-message-container">
                    <span key={messageIndex} className="optimizing-message">
                        {OPTIMIZING_MESSAGES[messageIndex]}
                    </span>
                </div>
            </div>
        </div>
    );
 };

export default function LandingPage() {
    const navigate = useNavigate();
    const [accessCode, setAccessCode] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showOptimizing, setShowOptimizing] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        if (!accessCode.trim()) {
            setError('Please enter your access code');
            return;
        }

        setIsLoading(true);

        try {
            const response = await checkAccess(accessCode.trim());

            if (response.success && response.sessionToken && response.variantId) {
                saveSession(response.sessionToken, response.variantId, response.batchNumber!);
                setShowOptimizing(true);
                setTimeout(() => {
                    setShowOptimizing(false);
                    navigate('/dashboard');
                }, 5000);
            } else {
                setError(response.message || 'Invalid access code');
            }
        } catch (err) {
            setError('Connection error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="landing-page">
            <AnimatedBackground />
            <div className="hero-gradient-overlay" />
            {showOptimizing && <OptimizingOverlay />}

            <nav className={`top-navbar ${isMenuOpen ? 'menu-open' : ''}`}>
                {isMenuOpen && <div className="navbar-overlay" onClick={() => setIsMenuOpen(false)}></div>}
                <div className="navbar-container">
                    <div className="navbar-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                        <BrandLogo size={36} />
                        <div className="navbar-logo-text">
                            <span>
                                web3lab
                                <span style={{ fontSize: '0.45em', opacity: 0.8, marginLeft: '2px', position: 'relative', top: '-2px' }}>.online</span>
                            </span>
                            <span className="navbar-logo-subtitle">training</span>
                        </div>
                    </div>

                    <button 
                        className={`mobile-menu-toggle ${isMenuOpen ? 'active' : ''}`}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>

                    <div className={`navbar-items ${isMenuOpen ? 'show' : ''}`}>
                        {NAV_ITEMS.map((item) => (
                            <a
                                key={item.name}
                                href={item.url}
                                target={item.isInternal ? "_self" : "_blank"}
                                rel={item.isInternal ? "" : "noopener noreferrer"}
                                className="navbar-item"
                                onClick={(e) => {
                                    if (item.isInternal) {
                                        e.preventDefault();
                                        setIsMenuOpen(false);
                                        navigate(item.url);
                                    }
                                }}
                            >
                                <span className="navbar-item-icon">
                                    <item.Icon />
                                </span>
                                <div className="navbar-item-content">
                                    <span className="navbar-item-name">{item.name}</span>
                                    <span className="navbar-item-desc">{item.description}</span>
                                </div>
                            </a>
                        ))}
                        
                        <div className="mobile-only-divider"></div>
                        
                        <a
                            href={JOIN_ITEM.url}
                            className="navbar-item navbar-item-special mobile-join-item"
                            onClick={(e) => {
                                e.preventDefault();
                                setIsMenuOpen(false);
                                navigate(JOIN_ITEM.url);
                            }}
                        >
                            <span className="navbar-item-icon">
                                <JOIN_ITEM.Icon />
                            </span>
                            <div className="navbar-item-content">
                                <span className="navbar-item-name">{JOIN_ITEM.name}</span>
                                <span className="navbar-item-desc">{JOIN_ITEM.description}</span>
                            </div>
                        </a>
                    </div>

                    <div className="navbar-items-right desktop-only">
                        <div className="navbar-divider-vertical"></div>
                        <a
                            href={JOIN_ITEM.url}
                            className="navbar-item navbar-item-special"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(JOIN_ITEM.url);
                            }}
                        >
                            <span className="navbar-item-icon">
                                <JOIN_ITEM.Icon />
                            </span>
                            <div className="navbar-item-content">
                                <span className="navbar-item-name">{JOIN_ITEM.name}</span>
                                <span className="navbar-item-desc">{JOIN_ITEM.description}</span>
                            </div>
                        </a>
                    </div>
                </div>
            </nav>

            <div className="landing-split">
                <div className="landing-hero">
                    <div className="hero-content">
                        <div className="hero-badge">
                            <span className="badge-dot"></span>
                            Join the Future
                        </div>

                        <h1 className="hero-title">
                            The Global Bridge<br />
                            <span className="text-gradient">Between Web3 and Society</span>
                        </h1>

                        <p className="hero-subtitle">
                            Driven by the Blockchain Community
                        </p>

                        <PresaleNotice onViewWhitepaper={() => navigate('/project-intro')} />

                        <div className="trust-section">
                            <div className="trust-headline">
                                <span className="trust-headline-prefix">Tailored For</span>

                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                                    <span className="trust-headline-number">5.5B+</span>
                                    <span className="trust-headline-suffix">Social Users</span>
                                </div>

                                <div className="stats-connection">
                                    <div className="connection-line"></div>
                                    <div className="stats-row">
                                        <span className="stats-number">800M+</span>
                                        <span className="stats-label">Crypto Natives</span>
                                    </div>
                                </div>
                            </div>

                            <div className="trust-stats">
                                <div className="trust-stat">
                                    <span className="trust-stat-value">205+</span>
                                    <span className="trust-stat-label">Countries</span>
                                </div>
                                <div className="trust-stat-divider"></div>
                                <div className="trust-stat">
                                    <span className="trust-stat-value">14+</span>
                                    <span className="trust-stat-label">Years old</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hero-decoration">
                        <div className="deco-circle deco-circle-1"></div>
                        <div className="deco-circle deco-circle-2"></div>
                        <div className="deco-circle deco-circle-3"></div>
                    </div>

                </div>

                <div className="landing-form-section landing-form-section-login">
                    <div className="landing-form-container">
                        <div className="form-header">
                            <h2>Welcome Back</h2>
                            <p>Enter your access code to start your journey</p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <label htmlFor="accessCode" className="input-label">
                                    Access Code
                                </label>
                                <input
                                    type="text"
                                    id="accessCode"
                                    className={`input ${error ? 'input-error' : ''}`}
                                    placeholder="Enter your code"
                                    value={accessCode}
                                    onChange={(e) => setAccessCode(e.target.value)}
                                    disabled={isLoading}
                                    autoComplete="off"
                                    autoFocus
                                />
                            </div>

                            {error && (
                                <div className="alert alert-error animate-fade-in">
                                    <span className="alert-icon">⚠️</span>
                                    <span>{error}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary btn-block btn-lg"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="spinner"></div>
                                        <span>Verifying...</span>
                                    </>
                                ) : (
                                    <span>Login (Beta)</span>
                                )}
                            </button>
                        </form>

                        <div className="form-divider">
                            <span>Trusted by millions worldwide</span>
                        </div>

                        <div className="form-features">
                            <div className="feature-item">
                                <span className="feature-icon">🔒</span>
                                <span>Secure Platform</span>
                            </div>
                            <div className="feature-item">
                                <span className="feature-icon">📚</span>
                                <span>Comprehensive Training</span>
                            </div>
                            <div className="feature-item">
                                <span className="feature-icon">🎓</span>
                                <span>Expert-Led Courses</span>
                            </div>
                        </div>

                        <div className="form-notice">
                            <span className="notice-icon">ℹ️</span>
                            <p>
                                This beta platform simulates and analyzes social credibility on the blockchain and is currently in an experimental testing phase.
                            </p>
                        </div>

                        <div className="form-footer">
                            <p>Don't have an access code? <a href="/join" onClick={(e) => { e.preventDefault(); navigate('/join'); }}>Join Us</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
